import { ExternalLink, FolderGit2, Globe } from 'lucide-react';
import type { ReactNode } from 'react';
import { Github, Linkedin } from '@/components/icons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getInitials } from '@/pages/Votes/components/logo';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { githubHandle } from '@/lib/utils';
import type { JudgeBallotProject } from '@/types';
import { ProjectLogo } from './ProjectLogo';

function LinkButton({
  href,
  icon: Icon,
  label,
}: {
  href: string;
  icon: typeof Globe;
  label: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 border-2 border-primary px-3 py-2 font-bold text-primary text-sm transition-colors hover:bg-primary hover:text-background"
    >
      <Icon className="h-4 w-4" />
      {label}
      <ExternalLink className="ml-auto h-3 w-3" />
    </a>
  );
}

function IconLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center border border-primary/60 text-primary transition-colors hover:bg-primary hover:text-background"
    >
      {children}
    </a>
  );
}

// The side panel a judge opens from a ballot row: everything about the project in one
// place — the pitch and description from the project's metadata JSON, links to the
// deploy and the repo, and the team.
export function ProjectSheet({
  project,
  onClose,
}: {
  project: JudgeBallotProject | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={project !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="theme-ph26 flex w-full flex-col gap-6 overflow-y-auto border-primary bg-background font-mono text-foreground sm:max-w-xl"
      >
        {project && (
          <>
            <SheetHeader className="flex flex-row items-center gap-4 pr-6 text-left">
              <ProjectLogo project={project} className="h-16 w-16" />
              <div className="min-w-0">
                <SheetTitle className="font-bold text-primary text-xl">
                  {project.order}. {project.name}
                </SheetTitle>
                <SheetDescription className="text-primary/70">
                  {project.teamSlug}
                  {project.trackName && ` · ${project.trackName}`}
                </SheetDescription>
              </div>
            </SheetHeader>

            {project.oneliner && project.oneliner !== project.description && (
              <p className="border-primary border-l-4 pl-3 font-bold text-primary">
                {project.oneliner}
              </p>
            )}

            {project.description && (
              <div className="flex flex-col gap-2">
                <h3 className="font-bold text-primary text-sm uppercase">
                  Descripción
                </h3>
                <p className="whitespace-pre-line text-foreground text-sm leading-relaxed">
                  {project.description}
                </p>
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              {project.deployUrl && (
                <LinkButton href={project.deployUrl} icon={Globe} label="Deploy" />
              )}
              {project.repoUrl && (
                <LinkButton
                  href={project.repoUrl}
                  icon={FolderGit2}
                  label="Repositorio"
                />
              )}
            </div>
            {!project.deployUrl && !project.repoUrl && (
              <p className="text-primary/60 text-sm">
                Este proyecto no tiene links registrados.
              </p>
            )}

            {project.members.length > 0 && (
              <div className="flex flex-col gap-2">
                <h3 className="font-bold text-primary text-sm uppercase">
                  Equipo
                </h3>
                <ul className="flex flex-col gap-2 text-sm">
                  {project.members.map((member) => {
                    const handle = githubHandle(member.github);
                    return (
                      <li
                        key={member.id}
                        className="flex items-center justify-between gap-2 border-primary/20 border-b pb-2 text-primary last:border-b-0"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar className="h-9 w-9 shrink-0 rounded-none border border-primary/40">
                            {handle && (
                              <AvatarImage
                                src={`https://github.com/${handle}.png?size=72`}
                                alt={`@${handle}`}
                              />
                            )}
                            <AvatarFallback className="rounded-none bg-primary/10 text-primary text-xs">
                              {getInitials(member.fullName)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 break-words">
                            {member.fullName}
                          </span>
                        </div>
                        <div className="flex shrink-0 gap-1">
                          {handle && (
                            <IconLink
                              href={`https://github.com/${handle}`}
                              label={`GitHub de ${member.fullName} (@${handle})`}
                            >
                              <Github className="h-4 w-4" />
                            </IconLink>
                          )}
                          {member.linkedin && (
                            <IconLink
                              href={member.linkedin}
                              label={`LinkedIn de ${member.fullName}`}
                            >
                              <Linkedin className="h-4 w-4" />
                            </IconLink>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
